import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  AppBar,
  Drawer as MuiDrawer,
  Hidden,
  IconButton,
  Toolbar,
  BottomNavigation,
  BottomNavigationAction,
} from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import TodayIcon from '@material-ui/icons/Today';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import CalendarToday from '@material-ui/icons/CalendarToday';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import CakeIcon from '@material-ui/icons/Cake';
import PersonIcon from '@material-ui/icons/Person';

import useDesktop from '../../hooks/useDesktop';
import useDiaryData from '../../hooks/useDiaryData';
import useQueryParams from '../../hooks/useQueryParams';

import Select from '../Select';
import Drawer from './Drawer';
import CreateTypeDialog from './CreateTypeDialog';
import { getDiaryYearOptions } from '../../utils';

const drawerWidth = 240;
const dateFormat = 'YYYY-MM-DD';

const sitemaps = [
  {
    label: 'Calendar',
    value: 'CALENDAR',
    pathname: '/calendar',
    icon: CalendarToday,
  },
  {
    label: 'Events',
    value: 'EVENTS',
    pathname: '/events',
    icon: FormatListBulleted,
  },
  {
    label: 'Birthdates',
    value: 'BIRTHDATES',
    pathname: '/birthdates',
    icon: CakeIcon,
  },
  {
    label: 'Profile',
    value: 'PROFILE',
    pathname: '/profile',
    icon: PersonIcon,
  },
];

const monthOptions = moment.monthsShort().map((e, i) => ({
  label: e,
  value: String(i),
}));

const yearOptions = getDiaryYearOptions();

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  bottomAppBar: {
    top: 'auto',
    bottom: 0,
    paddingBottom: 'max(14px, env(safe-area-inset-bottom))',
    backgroundColor: '#fbfbfb',
    borderTop: `1px solid #e0e0e0`,
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  toolbarWrapper: {
    paddingLeft: 10,
    paddingRight: 10,
    display: 'flex',
    justifyContent: 'space-between',
  },
  bottomToolbarWrapper: {
    //marginBottom: 'max(0px, env(safe-area-inset-bottom))!important',
  },
  drawerPaper: {
    width: drawerWidth,
    paddingBottom: 14,
  },
  navigateWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  monthSelect: {
    flexShrink: 0,
    marginTop: 0,
    marginBottom: 0,
    '& select, & div': {
      color: 'white',
    },
  },
  yearSelect: {
    flexShrink: 0,
    marginTop: 0,
    marginBottom: 0,
    '& select, & div': {
      color: 'white',
    },
  },
  content: {
    flexGrow: 1,
    width: `calc(100% - ${drawerWidth}px)`,
    padding: 0,
  },
  actionWrapper: {},
  container: {
    //minHeight: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
    //paddingBottom: 14,
    //backgroundColor: '#fbfbfb'
  },
  title: {
    fontSize: 18,
  },
}));

function Navbar(props) {
  const classes = useStyles();
  const routerHistory = useHistory();
  const routerLocation = useLocation();
  // eslint-disable-next-line
  const [queryParams, setQueryParams] = useQueryParams();
  const isDesktop = useDesktop('up', 'md');
  const [createType, setCreateType] = useState(false);
  // eslint-disable-next-line
  const [data, status, handleSetSelectedDate] = useDiaryData();

  const handleNextMonthClick = () => {
    const newMoment = moment(data.selectedDate).date(1).add(1, 'month');
    handleSetSelectedDate(newMoment.format(dateFormat));
  };

  const handlePrevMonthClick = () => {
    const newMoment = moment(data.selectedDate).date(1).add(-1, 'month');
    handleSetSelectedDate(newMoment.format(dateFormat));
  };

  const handleSetToday = () => {
    handleSetSelectedDate(moment().format(dateFormat));
    if (window.__scrollTo) {
      setTimeout(() => {
        window.__scrollTo(moment().format(dateFormat));
      }, 300);
    }
  };

  const handleMonthChange = (event, newValue) => {
    const newDate = moment(data.selectedDate)
      .month(newValue)
      .date(1)
      .format(dateFormat);
    handleSetSelectedDate(newDate);
  };

  const handleYearChange = (event, newValue) => {
    const newDate = moment(data.selectedDate)
      .year(newValue)
      .date(1)
      .format(dateFormat);
    handleSetSelectedDate(newDate);
  };

  const handlePageChange = (event, newValue) => {
    routerHistory.push(newValue);
  };

  const handleSearchClick = () => {
    setQueryParams({ 'search-diary': 'true' });
  };

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbarWrapper}>
          <div className={classes.navigateWrapper}>
            <IconButton
              color="inherit"
              size="small"
              onClick={handlePrevMonthClick}
            >
              <KeyboardArrowLeft />
            </IconButton>

            <Select
              className={classes.yearSelect}
              value={String(moment(data.selectedDate).year())}
              options={yearOptions}
              onChange={handleYearChange}
              maxWidth={isDesktop ? 100 : 80}
            />

            <Select
              className={classes.monthSelect}
              value={String(moment(data.selectedDate).month())}
              options={monthOptions}
              onChange={handleMonthChange}
              maxWidth={80}
            />

            <IconButton
              color="inherit"
              size="small"
              onClick={handleNextMonthClick}
            >
              <KeyboardArrowRight />
            </IconButton>
          </div>

          <div className={classes.actionWrapper}>
            <div>
              <IconButton
                color="inherit"
                className={classes.menuButton}
                onClick={handleSetToday}
              >
                <TodayIcon />
              </IconButton>
              <IconButton
                color="inherit"
                className={classes.menuButton}
                onClick={handleSearchClick}
              >
                <SearchIcon />
              </IconButton>
              <IconButton
                color="inherit"
                className={classes.menuButton}
                onClick={() => setCreateType(true)}
              >
                <AddIcon />
              </IconButton>
            </div>
          </div>
        </Toolbar>
      </AppBar>

      <nav className={classes.drawer}>
        <Hidden smDown implementation="css">
          <MuiDrawer
            classes={{ paper: classes.drawerPaper }}
            variant="permanent"
            open
          >
            <Drawer />
          </MuiDrawer>
        </Hidden>
      </nav>

      <main className={classes.content}>
        <div className={classes.toolbar} />
        <div className={classes.container}>{props.children}</div>
        {!isDesktop && <div style={{ minHeight: 56 }} />}
      </main>

      {!isDesktop && (
        <AppBar className={classes.bottomAppBar} position="fixed">
          <BottomNavigation
            value={routerLocation.pathname}
            onChange={handlePageChange}
            showLabels
          >
            {sitemaps.map(e => (
              <BottomNavigationAction
                key={e.value}
                value={e.pathname}
                label={e.label}
                icon={<e.icon />}
              />
            ))}
          </BottomNavigation>
        </AppBar>
      )}

      <CreateTypeDialog
        open={createType}
        onClose={() => setCreateType(false)}
      />
    </div>
  );
}

Navbar.propTypes = {
  container: PropTypes.any,
};

export default Navbar;
